<script setup lang="ts">
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import { SolrParamsKey } from '#shared/solr'
import { faArrowLeft, faArrowRight, faChevronDown, faChevronUp, faXmark } from '@fortawesome/pro-light-svg-icons'
import { onClickOutside, onKeyStroke } from '@vueuse/core'
import { transformShortTitle } from '~/utils/solr/law-utils'

const props = defineProps<{
  lawId: string
}>()

const emit = defineEmits<{
  close: []
}>()

const { $parseStringToDocument } = useNuxtApp()

const { previousView } = useLawView()
const { textLabelLower } = useLawTextLabels()

const backLabel = computed(() => {
  switch (previousView.value) {
    case 'curated':
      return 'Tilbake til utvalgte kilder'
    case 'literature':
      return 'Tilbake til relatert litteratur'
    case 'commentary':
      return 'Tilbake til lovkommentar'
    default:
      return `Tilbake til ${textLabelLower.value}`
  }
})

const panelRef = ref<HTMLElement | null>(null)
const expanded = ref<boolean>(true)

const lawIdOnly = computed(() => props.lawId.split('/')[0] ?? props.lawId)
const isLawLevelReference = computed(() => !props.lawId.includes('/'))

const contentParams = computed(() => ({ [SolrParamsKey.LawReferenceId]: props.lawId }))
const { data: contentData, isLoading: isContentLoading, error: contentError } = useLawCommentaryQuery('law-commentary', contentParams)

// Fetch base law for shortTitle - skip if already law-level reference
const lawParams = computed(() => isLawLevelReference.value ? {} : { [SolrParamsKey.LawReferenceId]: lawIdOnly.value })
const { data: lawData, isLoading: isLawLoading, error: lawError } = useLawCommentaryQuery('law-commentary', lawParams)

const isLoading = computed(() => isContentLoading.value || (isLawLevelReference.value ? false : isLawLoading.value))
const hasError = computed(() => contentError.value || lawError.value)

const document = computed(() => contentData.value?.response?.docs?.[0])
const lawTitle = computed(() => {
  const lawDocument = isLawLevelReference.value ? document.value : lawData.value?.response?.docs?.[0]
  return transformShortTitle(lawDocument?.shortTitle).title ?? lawDocument?.title ?? ''
})

/** Find the first section with bodytext content by traversing the law tree. */
function findFirstSectionWithContent(doc: LawAndCommentary | undefined): LawAndCommentary | undefined {
  if (!doc) {
    return undefined
  }

  if (doc.nodeType === 'section' || doc.nodeType === 'subsection') {
    const hasChildBodytext = doc.children?.some(child => child.bodytext)
    if (hasChildBodytext) {
      return doc
    }
  }

  if (doc.children?.length) {
    for (const child of doc.children) {
      const found = findFirstSectionWithContent(child as LawAndCommentary)
      if (found) {
        return found
      }
    }
  }

  return undefined
}

const displaySection = computed(() => {
  const doc = document.value
  if (!doc) {
    return undefined
  }

  if (doc.nodeType === 'section' && doc.children?.some(child => child.bodytext)) {
    return doc
  }

  if (doc.nodeType === 'law' || doc.nodeType === 'chapter') {
    return findFirstSectionWithContent(doc)
  }

  return doc
})

const paragraphContent = computed(() => {
  const section = displaySection.value
  const childBodytext = section?.children?.find(child => child.bodytext)?.bodytext ?? ''
  const bodytext = section?.children?.length ? childBodytext : section?.bodytext ?? ''

  if (!bodytext) {
    return ''
  }

  const parsedDoc = $parseStringToDocument(bodytext, ['clean-corrupted-attributes', 'process-links'])
  return parsedDoc.body.innerHTML
})

const sectionTitle = computed(() => displaySection.value?.title)

// Show the referenced title below the law name, with a content preview for law and chapter references
const referenceTitle = computed(() => {
  if (isLawLevelReference.value) {
    return undefined
  }
  if (document.value?.title) {
    return document.value.title
  }
  // Use the paragraph from the ID when a subsection has no title
  const paragraf = props.lawId.toLowerCase().split('/').find(segment => segment.startsWith('paragraf-'))
  return paragraf ? `§ ${paragraf.slice('paragraf-'.length)}` : undefined
})
const showsContentPreview = computed(() =>
  document.value?.nodeType === 'law' || document.value?.nodeType === 'chapter',
)

function handleClose() {
  emit('close')
}

onKeyStroke('Escape', handleClose)
// Exclude collapse buttons in ReadingLayout
onClickOutside(panelRef, handleClose, { ignore: ['[data-sidebar-toggle]', '[data-content-toggle]'] })
</script>

<template>
  <aside ref="panelRef" class="animate-slide-up flex flex-col gap-m" aria-label="Lovhenvisning">
    <div class="flex nav:hidden items-center gap-2xs">
      <ControlButton size="small" :icon="faArrowLeft" :aria-label="backLabel" class="self-start bg-transparent hover:bg-transparent border rounded-lg" @click="handleClose" />
      <span class="text-xsmall" aria-hidden="true">{{ backLabel }}</span>''
    </div>
    <div v-if="isLoading" class="flex flex-col gap-m" aria-live="polite" aria-busy="true">
      <Skeleton class="h-6 w-48" />
      <Skeleton class="h-4 w-full" />
      <Skeleton class="h-4 w-full" />
      <Skeleton class="h-4 w-3/4" />
    </div>

    <div v-else-if="hasError" class="flex justify-between items-center text-sm text-red-600" role="alert">
      <span>Kunne ikke laste lovhenvisning.</span>
      <ControlButton size="small" :icon="faXmark" class="p-xs shadow-sm rounded-lg bg-white dark:bg-carbon-400 [&>svg]:fill-blurple-500 dark:[&>svg]:fill-carbon-50 [&>svg]:!size-5 hover:bg-transparent" @click="handleClose" />
    </div>

    <template v-else-if="document">
      <div class="flex flex-col gap-m items-start">
        <div class="flex items-center justify-between w-full">
          <h2 class="text-small font-bold flex-1">
            Kilde
          </h2>
        </div>
        <div class="flex flex-col gap-m w-full">
          <div class="flex items-center py-xs">
            <div class="flex flex-col gap-3xs flex-grow">
              <span class="text-caption text-secondary">
                Henvisning&nbsp;til&nbsp;lov
              </span>
              <span class="flex items-start gap-3xs text-small">
                <span class="flex flex-col">
                  <span>{{ lawTitle }}</span>
                  <span v-if="referenceTitle" class="text-secondary text-xsmall">{{ referenceTitle }}</span>
                </span>
              </span>
            </div>
            <div class="flex items-center gap-2xs">
              <ControlButton aria-controls="reference-content" :aria-label="expanded ? 'Skjul lovreferanse' : 'Utvid lovreferanse'" :aria-expanded="expanded" :selected="expanded" type="outlined" :icon="expanded ? faChevronUp : faChevronDown" @click="expanded = !expanded" />
              <ControlButton aria-label="Fjern lovreferanse" type="outlined" :icon="faXmark" @click="emit('close')" />
            </div>
          </div>

          <div v-if="expanded" id="reference-content" class="flex flex-col gap-s animate-slide-down">
            <p v-if="showsContentPreview && sectionTitle" class="text-small font-medium">
              {{ sectionTitle }}
            </p>
            <div v-if="paragraphContent" class="text-small max-h-52 overflow-y-auto" v-html="paragraphContent" />
            <ControlButton aria-label="Vis hele loven (åpnes i ny fane)" type="outlined" :icon="faArrowRight" :to="{ name: 'law-by-id', params: { lovId: [lawIdOnly] } }" size="large" target="_blank" class="gap-xs dark:[&>svg]:fill-carbon-50" @click="handleClose">
              Les&nbsp;hele&nbsp;loven
            </ControlButton>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="flex justify-between items-center text-sm">
      <span class="text-sm text-carbon-500">
        Kunne&nbsp;ikke&nbsp;finne&nbsp;lovhenvisning
      </span>
      <ControlButton size="small" :icon="faXmark" class="p-xs shadow-sm rounded-lg bg-white dark:bg-carbon-400 [&>svg]:fill-blurple-500 dark:[&>svg]:fill-carbon-50 [&>svg]:!size-5 hover:bg-transparent" @click="handleClose" />
    </div>
  </aside>
</template>
